import { useState, useCallback } from 'react';

/**
 * useGeolocation
 *
 * Thin wrapper around the free HTML5 Geolocation API (navigator.geolocation).
 * No Google/Mapbox or any paid map service is used.
 *
 * Usage:
 *   const { requesting, requestLocation } = useGeolocation();
 *   const result = await requestLocation();
 *   // result: { granted: boolean, coordinates?: [lng, lat], error?: string }
 *
 * Coordinates are returned as [longitude, latitude] to match the GeoJSON order
 * used by MongoDB on the backend.
 */
const useGeolocation = () => {
  const [requesting, setRequesting] = useState(false);

  const isSupported =
    typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const requestLocation = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        if (!isSupported) {
          resolve({ granted: false, error: 'unsupported' });
          return;
        }

        setRequesting(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setRequesting(false);
            resolve({
              granted: true,
              coordinates: [pos.coords.longitude, pos.coords.latitude],
              accuracy: pos.coords.accuracy
            });
          },
          (err) => {
            setRequesting(false);
            resolve({
              granted: false,
              error:
                err && err.code === err.PERMISSION_DENIED
                  ? 'denied'
                  : 'unavailable'
            });
          },
          {
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 60000,
            ...options
          }
        );
      }),
    [isSupported]
  );

  return { requesting, requestLocation, isSupported };
};

export default useGeolocation;
